import {ENV} from 'config/ENV.config'

/**
 * Set the browser tab title from an explicit title, else from the URL path.
 *
 * This — not the `<title>` in public/index.html — is what the user actually sees: it runs from
 * PrivateRoutes on every navigation, so it overwrites the interpolated markup as soon as React
 * mounts. The brand suffix therefore has to be applied here, or a rebranded build still reads
 * "ErpBoi" everywhere except the first paint.
 */
export const setWindowTitle = (title: string = null) => {
  const t = title || window.location.pathname?.split('/')?.slice(1)?.join(' - ')
  const page = t ? t.charAt(0).toUpperCase() + t.slice(1) : ''
  const suffix = `${ENV.appName} | ${ENV.brandName}`
  document.title = page ? `${page} | ${suffix}` : suffix
}

export const toAbsoluteUrl = (pathname: string) => process.env.PUBLIC_URL + pathname

// Uploaded files are stored as "file/show/<companyId>/..." and served by hrm-service's public
// /file/show route. Worker photos and documents written before the split were uploaded through
// hrm-service, and labor-service writes into the same core FileService bucket on the same host, so
// the hrm gateway serves every one of them.
export const getServerFile = (fileUrl) => {
  if (fileUrl) {
    return ENV.hrmApiEndpoint + '/' + fileUrl
  }
  return ''
}

export const getUrlFromJson = (jsonStr) => {
  if (jsonStr) {
    // Rows saved before /home/bucket_parent was provisioned hold an empty `{}` (no url) — guard so
    // we don't build ".../undefined".
    const url = JSON.parse(jsonStr)?.url
    if (url) return getServerFile(url)
  }
  return ''
}

// Force-download a server file stored as a {url,path} JSON string. Appends ?download=1 so the
// serving endpoint sends `Content-Disposition: attachment`; `baseName` (e.g. the file type) becomes
// the saved filename with the original extension. Returns false when the file has no url.
export const downloadServerFile = (jsonStr, baseName?: string) => {
  const url = getUrlFromJson(jsonStr)
  if (!url) return false
  const ext = url.split('?')[0].split('.').pop()
  const name = baseName && ext ? `${baseName}.${ext}` : ''
  const a = document.createElement('a')
  a.href = url + (url.includes('?') ? '&' : '?') + 'download=1' + (name ? '&name=' + encodeURIComponent(name) : '')
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  return true
}

export const getUrlFromJsonArray = (jsonArrStr) => {
  if (jsonArrStr) {
    return getServerFile(JSON.parse(jsonArrStr)[0].url)
  }
  return ''
}

export const getCurrentMonth = () => {
  var month = new Date().getMonth() // js month start with 0
  if (month < 10) return '0' + month
  else return month
}

export const getCurrentJavaMonth = () => {
  var month = new Date().getMonth() + 1 // js month start with 0, but java month start with 1
  if (month < 10) return '0' + month
  else return month
}

export const getGenderOptions = [
  {type: 'Male', value: 'MALE'},
  {type: 'Female', value: 'FEMALE'},
  {type: 'Other', value: 'OTHER'},
]

export const getCurrencyOptions = [
  {type: 'USD', value: 'USD'},
  {type: 'EURO', value: 'EURO'},
  {type: 'RUPEE', value: 'RUPEE'},
  {type: 'JPY', value: 'JPY'},
  {type: 'RMB', value: 'RMB'},
  {type: 'SAR', value: 'SAR'},
  {type: 'AED', value: 'AED'},
  {type: 'OMR', value: 'OMR'},
  {type: 'BDT', value: 'BDT'},
]

export const getSocialMediaOpts = [
  {type: 'Linkedin', value: 'LINKEDIN'},
  {type: 'Facebook', value: 'FACEBOOK'},
  {type: 'Stackoverflow', value: 'STACKOVERFLOW'},
  {type: 'HackerRank', value: 'HACKERRANK'},
  {type: 'Github', value: 'GITHUB'},
  {type: 'Website', value: 'WEBSITE'},
  {type: 'Other', value: 'OTHER'},
]

export const priorityOptions = [
  {name: 'Low', value: '1'},
  {name: 'Medium', value: '2'},
  {name: 'High', value: '3'},
  {name: 'Critical', value: '4'},
]

export const employeeFileTypes = [
  {name: 'CV/Resume', value: 'CV'},
  {name: 'NID/Citizen ID', value: 'NID'},
  {name: 'Passport', value: 'PASSPORT'},
  {name: 'Driving License', value: 'DRIVING_LC'},
  {name: 'Birth Certificate', value: 'BIRTH_CERT'},
]

export const monthOptions = [
  {name: 'January', value: '01'},
  {name: 'February', value: '02'},
  {name: 'March', value: '03'},
  {name: 'April', value: '04'},
  {name: 'May', value: '05'},
  {name: 'June', value: '06'},
  {name: 'July', value: '07'},
  {name: 'August', value: '08'},
  {name: 'September', value: '09'},
  {name: 'October', value: '10'},
  {name: 'November', value: '11'},
  {name: 'December', value: '12'},
]

export const monthShortOptions = [
  {name: 'Jan', value: '01'},
  {name: 'Feb', value: '02'},
  {name: 'Mar', value: '03'},
  {name: 'Apr', value: '04'},
  {name: 'May', value: '05'},
  {name: 'Jun', value: '06'},
  {name: 'Jul', value: '07'},
  {name: 'Aug', value: '08'},
  {name: 'Sep', value: '09'},
  {name: 'Oct', value: '10'},
  {name: 'Nov', value: '11'},
  {name: 'Dec', value: '12'},
]

/**
 * Worked duration between two shift times as "HH:mm", wrapping midnight.
 *
 * A night shift (22:00 -> 06:00) is 8 hours, not 16. The previous version took |inHour - outHour|,
 * which inverted across midnight AND dropped the minutes outright — inMin/outMin were parsed and
 * never used, so 09:30 -> 18:00 came back as "9:00" instead of "08:30".
 *
 * Accepts 24-hour "HH:mm" (what the <input type='time'> fields emit) and the legacy "hh:mm AM/PM".
 */
export const calcDuration = (inTime: string, outTime: string) => {
  const minutes = (value: string): number => {
    const [clock, meridiem] = (value || '').trim().split(/\s+/)
    const [h, m] = (clock || '').split(':').map((n) => parseInt(n, 10))
    if (isNaN(h) || isNaN(m)) return NaN

    let hour = h
    const mer = (meridiem || '').toUpperCase()
    if (mer === 'PM' && hour < 12) hour += 12
    if (mer === 'AM' && hour === 12) hour = 0
    return hour * 60 + m
  }

  const from = minutes(inTime)
  const to = minutes(outTime)
  if (isNaN(from) || isNaN(to)) return '00:00'

  const elapsed = (((to - from) % 1440) + 1440) % 1440 // wraps midnight; always [0, 24h)
  const pad = (n: number) => String(n).padStart(2, '0')

  return `${pad(Math.floor(elapsed / 60))}:${pad(elapsed % 60)}` // HH:mm
}
